import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/common";
import { Button } from "@/components/ui/button";
import { IdentityVerification } from "@/components/IdentityVerification";
import { PartyPopper, ShieldCheck, Sparkles, ArrowRight, Check } from "lucide-react";

function Step({ n, title, children, done }) {
    return (
        <div className="flex gap-3">
            <div className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-700 ${done ? "bg-primary text-primary-foreground" : "bg-white/10 text-white"}`}>
                {done ? <Check className="h-4 w-4" /> : n}
            </div>
            <div className="min-w-0 flex-1">
                <p className="font-600 text-white">{title}</p>
                <div className="mt-1 text-sm text-white/70">{children}</div>
            </div>
        </div>
    );
}

export default function Welcome() {
    const { user, checked } = useAuth();
    const navigate = useNavigate();
    const isMember = checked && user && user.role === "member";

    return (
        <div className="min-h-screen bg-background text-foreground">
            <header className="border-b border-white/10">
                <div className="mx-auto flex max-w-3xl items-center justify-between px-5 py-4">
                    <Logo />
                    <Link to="/" className="text-sm text-white/60 hover:text-white">Home</Link>
                </div>
            </header>

            <main className="mx-auto max-w-3xl px-5 py-12">
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
                    <span className="inline-flex items-center gap-2 rounded-full bg-accent/15 px-4 py-1.5 text-sm font-600 text-accent">
                        <PartyPopper className="h-4 w-4" /> Your FREE first month is on its way
                    </span>
                    <h1 className="mt-5 font-display text-4xl font-700 leading-tight tracking-tight text-white sm:text-5xl" data-testid="welcome-heading">
                        Welcome — let's finish your profile
                    </h1>
                    <p className="mt-4 max-w-xl text-base text-white/75">
                        Thanks for joining Single Social Club! Once your PayPal payment completes, just two quick steps
                        stand between you and your first group: get verified, and tell us what you love to do.
                    </p>
                </motion.div>

                {isMember ? (
                    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.1 }} className="mt-10 space-y-8">
                        <div className="space-y-5 rounded-2xl border border-white/10 bg-white/5 p-6">
                            <Step n={1} title="Membership started" done>
                                Your FREE first month is active (renews at $20/month — cancel anytime from your profile).
                            </Step>
                            <Step n={2} title="Get verified — upload your ID" done={user.verification_status === "verified"}>
                                {user.verification_status === "verified"
                                    ? "You're verified and ready to be matched."
                                    : "Upload a Driver's License, State ID or Passport below. It's private and only seen by our verification team."}
                            </Step>
                            <Step n={3} title="Finish your profile">
                                Pick the activities you love and when you're free so we can match you into a group.
                            </Step>
                        </div>

                        {user.verification_status !== "verified" && (
                            <div data-testid="welcome-verify">
                                <div className="mb-3 flex items-center gap-2 text-white">
                                    <ShieldCheck className="h-5 w-5 text-accent" />
                                    <h2 className="text-lg font-700">Get verified</h2>
                                </div>
                                <IdentityVerification />
                            </div>
                        )}

                        <div className="flex flex-wrap gap-3">
                            {!user.onboarding_complete
                                ? <Button size="lg" data-testid="welcome-finish-setup" onClick={() => navigate("/onboarding")} className="cta-glow rounded-full bg-accent text-accent-foreground hover:bg-accent/90 px-8">
                                      Finish setup <ArrowRight className="ml-1 h-4 w-4" />
                                  </Button>
                                : <Button size="lg" data-testid="welcome-go-dashboard" onClick={() => navigate("/app")} className="cta-glow rounded-full bg-accent text-accent-foreground hover:bg-accent/90 px-8">
                                      Go to my dashboard <ArrowRight className="ml-1 h-4 w-4" />
                                  </Button>}
                            <Button size="lg" variant="outline" onClick={() => navigate("/profile?tab=identity")} className="rounded-full border-white/30 bg-white/5 px-8 text-white hover:bg-white/15">
                                Manage from profile
                            </Button>
                        </div>
                    </motion.div>
                ) : (
                    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.1 }} className="mt-10 space-y-6" data-testid="welcome-guest">
                        <div className="space-y-5 rounded-2xl border border-white/10 bg-white/5 p-6">
                            <Step n={1} title="Create your account">
                                Set up your login so we can save your profile and verification.
                            </Step>
                            <Step n={2} title="Get verified — upload your ID">
                                After signing in you'll upload a Driver's License, State ID or Passport. Private, and only
                                seen by our verification team.
                            </Step>
                            <Step n={3} title="Tell us what you love & when you're free">
                                We match you into a small group around a shared activity.
                            </Step>
                        </div>
                        <div className="flex flex-wrap gap-3">
                            <Link to="/register">
                                <Button size="lg" data-testid="welcome-create-account" className="cta-glow rounded-full bg-accent text-accent-foreground hover:bg-accent/90 px-8">
                                    Create your account <ArrowRight className="ml-1 h-4 w-4" />
                                </Button>
                            </Link>
                            <Link to="/login">
                                <Button size="lg" variant="outline" data-testid="welcome-signin" className="rounded-full border-white/30 bg-white/5 px-8 text-white hover:bg-white/15">
                                    I already have an account
                                </Button>
                            </Link>
                        </div>
                        <p className="flex items-center gap-2 text-sm text-white/60">
                            <Sparkles className="h-4 w-4 text-accent" /> Your FREE first month starts as soon as your PayPal payment completes.
                        </p>
                    </motion.div>
                )}
            </main>
        </div>
    );
}
